import datasetsConfig from '@/data/datasets.json'

export const DEFAULT_TOP_N = 5
export const MIN_TOP_N = 1
export const MAX_TOP_N = 100

/**
 * Views whose data is a flat "category vs count" list, so keeping only the N
 * highest counts is meaningful. Deliberately excluded: timeseries/heatmap/
 * scatter (the axis is not a single category) and the raw data views
 * (csv/dataframe/json), where `limit` already covers the need.
 */
export const TOP_N_SUPPORTED_VIEWS = Object.freeze(['bar', 'ring', 'dona'])

/**
 * Datasets the control is offered for. An explicit allow-list, not derived
 * from the filter slots: many datasets could technically be ranked, but the
 * feature is only wanted on these two.
 *
 * Both are live: `alarms` arrived with its own module in the staging merge of
 * 2026-08-18 (default field `units.description` → `id_unit`, views bar/dona/ring).
 */
export const TOP_N_SUPPORTED_DATASETS = Object.freeze(['operational_security', 'alarms'])

/** Filter slots that never identify a chart category. */
const NON_CATEGORY_FILTER_PARAMS = new Set(['time'])

export function supportsTopN(view) {
  return TOP_N_SUPPORTED_VIEWS.includes(view)
}

export function supportsTopNDataset(datasetKey) {
  return TOP_N_SUPPORTED_DATASETS.includes(datasetKey)
}

export function normalizeTopN(value) {
  if (value === '' || value === null || value === undefined) return null

  const numericValue = Number(value)
  if (!Number.isFinite(numericValue) || numericValue < MIN_TOP_N) return null

  return Math.min(Math.floor(numericValue), MAX_TOP_N)
}

/**
 * The chart's `by` may carry a second field (bar colour). Only the first one
 * is the category being ranked.
 */
export function primaryByColumn(byParam) {
  if (!byParam) return ''
  return String(byParam).split(',')[0].trim()
}

const shortColumn = column => String(column).split('.').pop()

/**
 * Read a column out of a ranking row. The backend was measured to key the rows
 * by the qualified column name, but every sibling reader of this endpoint
 * hedges with the short name too (see useChartFilterOptions).
 */
const readColumn = (row, column) => (
  row?.[column] !== undefined ? row[column] : row?.[shortColumn(column)]
)

/**
 * How to restrict the chart to a set of ranked categories.
 *
 * `datasets.json[dataset].filters` maps param name -> qualified column, and the
 * backend resolves those params itself. The `where` parameter is NOT usable for
 * this: on operational_security it changes the grain of `$count` (23577 without
 * it versus 1033 with an always-true condition), so it would silently rewrite
 * the numbers it is supposed to filter.
 *
 * The id slot of the ranked column's own table wins when it exists, because
 * measurements showed it is both unique and reliable, while some name-valued
 * slots are dead: on operational_security `user=Supervisor 01` returns zero
 * rows while `id_user=1219` is exact.
 *
 * A dataset can also name the slot itself through its `rankingTargets` map, for
 * a column whose id lives in a table other than its own — the rule below cannot
 * guess that. On `alarms`, `type_alarms.description` is such a case: the id of
 * the ranked label is `alarms.type`, and its name slot is worse than useless
 * (`type=<name>` is cast to 0 by the backend and silently returns the rows of
 * type 0 instead of none).
 *
 * @returns {{param: string, valueColumn: string, labelColumn: string}|null}
 */
export function resolveRankingTarget(datasetKey, column, overrides = null) {
  const filters = datasetsConfig[datasetKey]?.filters
  if (!filters || !column) return null

  const override = overrides?.[column]
  if (override?.param && override?.valueColumn) {
    // Only honour a param the dataset actually declares: an unknown one is
    // ignored by the backend with a 200 and the full list, which is
    // indistinguishable from 'no filter'. Better to report it unavailable.
    if (!Object.prototype.hasOwnProperty.call(filters, override.param)) return null
    return { param: override.param, valueColumn: override.valueColumn, labelColumn: column }
  }

  // Availability: the dataset must declare THIS column as a filter. That is what
  // separates a category from a date or a free-text field — without this gate,
  // any column of a table that happens to own an `id_*` slot would qualify,
  // including `…form_answers.created_at`.
  const nameParam = Object.keys(filters)
    .filter(param => filters[param] === column)
    .filter(param => !NON_CATEGORY_FILTER_PARAMS.has(param) && !param.startsWith('id_'))
    .sort()[0]
  if (!nameParam) return null

  // Mechanism: prefer the id slot of the ranked column's own table.
  const idColumn = `${column.split('.')[0]}.id`
  const idParam = Object.keys(filters)
    .filter(param => param.startsWith('id_') && filters[param] === idColumn)
    .sort()[0]
  if (idParam) return { param: idParam, valueColumn: idColumn, labelColumn: column }

  return { param: nameParam, valueColumn: column, labelColumn: column }
}

/**
 * Serialize the winning values for a dataset filter param.
 *
 * Comma-joining is what every other filter of this component already sends and
 * keeps the URL readable, but it cannot carry a value that contains a comma —
 * those fall back to the JSON array form the backend also accepts.
 */
export function serializeFilterValues(values) {
  const clean = values.filter(v => v !== null && v !== undefined && v !== '')
  if (!clean.length) return ''

  const needsJson = clean.some(v => String(v).includes(','))
  return needsJson ? JSON.stringify(clean) : clean.join(',')
}

/**
 * Rewrite a chart URL into the aggregate-count query that ranks the categories.
 *
 * `columns=<cols>,$count` returns one row per distinct value with its record
 * count, honouring the very same filters and time range the chart carries — so
 * the ranking can never disagree with the chart it feeds.
 */
export function buildRankingUrl(chartUrl, target) {
  if (!chartUrl || !target?.labelColumn) return ''

  const [base, query = ''] = chartUrl.split('/?')
  const params = new URLSearchParams(query)

  const columns = [...new Set([target.valueColumn, target.labelColumn])].filter(Boolean)
  params.set('view', 'json')
  params.set('columns', `${columns.join(',')},$count`)
  params.set('limit', '-1')
  params.set('plaintext', 'true')
  params.set('live', 'false')

  // Chart-only params: harmless but pointless on an aggregate read.
  ;['by', 'freq', 'stack', 'orientation', 'legendPosition', 'innerRadius',
    'height', 'REFRESH_INTERVAL', 'engine', 'mapping', 'trendline',
  ].forEach(key => params.delete(key))

  return `${base}/?${params.toString().replace(/%2C/gi, ',')}`
}

/**
 * Pick the filter values of the N labels with the highest count.
 *
 * Counts are aggregated per LABEL because the endpoint keys its rows on the id,
 * so one label can arrive split across several rows (measured: 68 rows for 66
 * distinct user names) and the chart draws those as a single bar. Every id
 * behind a winning label is returned, so the restricted chart keeps that bar
 * whole. Empty labels or values are dropped: they cannot be expressed as a
 * filter value, so keeping them would return fewer bars than requested.
 */
export function pickTopFilterValues(rows, target, n) {
  if (!Array.isArray(rows) || !target?.labelColumn) return []

  const byLabel = new Map()
  rows.forEach(row => {
    const label = readColumn(row, target.labelColumn)
    const value = readColumn(row, target.valueColumn)
    const count = Number(row?.count)
    if (label === null || label === undefined || label === '') return
    if (value === null || value === undefined || value === '') return
    if (!Number.isFinite(count)) return

    const key = String(label)
    const entry = byLabel.get(key) || { count: 0, values: [] }
    entry.count += count
    entry.values.push(String(value))
    byLabel.set(key, entry)
  })

  return [...byLabel.values()]
    .sort((a, b) => b.count - a.count)
    .slice(0, n)
    .reduce((all, entry) => all.concat(entry.values), [])
}
